import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import { useLocation } from "react-router-dom";
import NavItem from "../common_components/NavItem";
import Popover from "@mui/material/Popover";
import { ROUTES } from "../routes/routes.constants";
import { resolveSidebarModuleBasePath } from "./moduleResolver";
import { useDispatch } from "react-redux";
import { setActiveModuleKey } from "../redux/userDataSlice";
/**
 * Recursive component to handle nested menu items (parent -> child -> child)
 * Supports unlimited nesting levels
 */
function MenuItemWithChildren({ item, pathname, level = 0 }) {
    const location = useLocation();
    const currentPathname = location.pathname;
    // Owning module base route for the current path (for multi-page modules), so
    // a sub-page keeps its menu item highlighted. Sidebar-only resolver (keeps the
    // shared /account-overview drill-in highlighting its module without touching the
    // header title). Falls back to the path itself.
    const activeModulePath = resolveSidebarModuleBasePath(currentPathname) ?? currentPathname;
    const hasChildren = item.items && item.items.length > 0;
    const dispatch = useDispatch();
    // Helper function to check if pathname matches roles permission routes
    const isRolesPermissionRoute = (path) => {
        // Extract base path from edit route (remove :id parameter)
        const editRouteBase = ROUTES.rolePermissionEdit.path.replace('/:id', '');
        return path === ROUTES.rolesPermission.path ||
            path.startsWith(ROUTES.rolePermissionCreate.path) ||
            path.startsWith(editRouteBase + '/');
    };
    // Helper function to check if pathname matches Meta Ad Audit routes
    const isMetaAuditRoute = (path) => {
        const reportRouteBase = ROUTES.metaAdAuditDetail.path.replace('/:reportId', '');
        return path === ROUTES.metaAdAudit.path || path.startsWith(reportRouteBase + '/');
    };
    const [anchorEl, setAnchorEl] = useState(null);
    const menuItemRef = useRef(null);
    const handlePopoverToggle = (event) => {
        event.stopPropagation();
        if (anchorEl) {
            setAnchorEl(null);
        }
        else {
            setAnchorEl(event.currentTarget);
        }
    };
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    // Close popover when pathname changes (navigation happens)
    useEffect(() => {
        if (anchorEl) {
            handlePopoverClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPathname]);
    // If item has direct link and no children, render as simple nav item
    if (item.to && !hasChildren) {
        const isActive = currentPathname === item.to ||
            item.to === activeModulePath ||
            (item.to === ROUTES.rolesPermission.path && isRolesPermissionRoute(currentPathname)) ||
            (item.to === ROUTES.metaAdAudit.path && isMetaAuditRoute(currentPathname));
        return (_jsx("div", { className: "main-sub-menu-container", children: _jsx(NavItem, { to: item.to, icon: item.icon, label: item.label, isActive: isActive, isCollapsed: false, onClick: () => {
                    // Explicitly set active module key (no route-based module detection)
                    dispatch(setActiveModuleKey(item.key));
                } }) }));
    }
    // If item has children, render as expandable item
    if (hasChildren) {
        const checkActiveChild = (items) => {
            return items.some((child) => {
                if (child.to === currentPathname)
                    return true;
                if (child.to && child.to === activeModulePath)
                    return true;
                // Special handling for roles permission routes
                if (child.to === ROUTES.rolesPermission.path && isRolesPermissionRoute(currentPathname)) {
                    return true;
                }
                // Special handling for meta audit routes
                if (child.to === ROUTES.metaAdAudit.path && isMetaAuditRoute(currentPathname)) {
                    return true;
                }
                if (child.items && child.items.length > 0) {
                    return checkActiveChild(child.items);
                }
                return false;
            });
        };
        const hasActiveChild = checkActiveChild(item.items);
        // If parent has URL, wrap in NavItem, otherwise just expandable
        if (item.to) {
            const isActive = currentPathname === item.to ||
                item.to === activeModulePath ||
                (item.to === ROUTES.rolesPermission.path && isRolesPermissionRoute(currentPathname)) ||
                (item.to === ROUTES.metaAdAudit.path && isMetaAuditRoute(currentPathname));
            return (_jsxs("div", { className: "main-sub-other-menu-container", children: [_jsx(Box, { ref: menuItemRef, onClick: (e) => {
                            if (hasChildren) {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePopoverToggle(e);
                            }
                        }, sx: { position: 'relative' }, children: _jsx(NavItem, { to: item.to, icon: item.icon, label: item.label, isActive: isActive, isCollapsed: false, onClick: (e) => {
                                // If this parent has its own route, treat click as module activation
                                dispatch(setActiveModuleKey(item.key));
                                if (hasChildren) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                }
                            } }) }), hasChildren && (_jsx(Popover, { open: open, anchorEl: anchorEl, onClose: handlePopoverClose, anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'right',
                        }, transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        }, PaperProps: {
                            sx: {
                                minWidth: 200,
                                maxHeight: 400,
                                overflow: 'auto',
                                mt: 0.5,
                            },
                        }, children: _jsx(List, { disablePadding: true, children: item.items.map((child) => (_jsx(MenuItemWithChildren, { item: child, pathname: pathname, level: level + 1 }, child.key))) }) }))] }));
        }
        return (_jsxs("div", { className: "under-sub-menu", children: [_jsxs(Box, { ref: menuItemRef, onClick: handlePopoverToggle, className: hasActiveChild ? "is-active" : "", sx: {
                        display: "flex",
                        alignItems: "center",
                        padding: "10px",
                        gap: "10px",
                        margin: "0",
                        border: "1px solid transparent",
                        cursor: "pointer",
                        "&:hover": {
                            bgcolor: "#E4E4E7",
                            borderRadius: "6.4px",
                        },
                    }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", flex: 1, gap: '10px', minWidth: 0 }, children: [_jsx(Box, { sx: {
                                        minWidth: 'unset',
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: hasActiveChild ? "#FF008E" : "text.secondary",
                                    }, children: item.icon ? _jsx(item.icon, { fontSize: "small" }) : null }), _jsx(Box, { component: "span", sx: {
                                        flex: 1,
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        color: hasActiveChild ? "#FF008E" : "inherit",
                                        ml: 0,
                                    }, children: item.label })] }), hasChildren && (_jsx(IconButton, { size: "small", sx: {
                                width: 24,
                                height: 24,
                                color: hasActiveChild ? "#FF008E" : "text.secondary",
                            }, children: _jsx(KeyboardArrowRightOutlinedIcon, { fontSize: "small" }) }))] }), hasChildren && (_jsx(Popover, { className: "sub-menu-popover-sidebar", open: open, anchorEl: anchorEl, onClose: handlePopoverClose, anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    }, transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    }, PaperProps: {
                        sx: {
                            minWidth: 200,
                            maxHeight: 400,
                            overflow: 'auto',
                            mt: 0.5,
                        },
                    }, children: _jsx(List, { disablePadding: true, children: item.items.map((child) => (_jsx(MenuItemWithChildren, { item: child, pathname: pathname, level: level + 1 }, child.key))) }) }))] }));
    }
    // Fallback: render as simple item even without link
    return (_jsx(Box, { sx: { px: 1, py: 0.5 }, children: _jsxs(Box, { sx: { display: "flex", alignItems: "center" }, children: [_jsx(Box, { sx: {
                        minWidth: 'unset',
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "text.secondary",
                    }, children: item.icon ? _jsx(item.icon, { fontSize: "small" }) : null }), _jsx(Box, { component: "span", sx: {
                        fontSize: "0.875rem",
                        color: "text.secondary",
                        ml: 1,
                    }, children: item.label })] }) }));
}
export default MenuItemWithChildren;
